import React, { useEffect, useState } from 'react';
import { Localizer } from 'services/Localizer';
import { ProductPricingLine, ProductPricingLinesDetails } from './types';

interface ProductPricingLinesProps {
	details?: ProductPricingLinesDetails;
	onPricingLineChange: (productPricingLine: CheckableLine) => void;
	className?: string;
}

export type CheckableLine =
	| {
			/**
			 * This is always populated if the product is for sale.
			 * Under certain circumstances, it may be undefined/null.
			 * This isn't desirable and will probably change in the future.
			 */
			readonly pricingLine: ProductPricingLine | undefined;
			readonly isInitialization: boolean;
			readonly checked: boolean;
	  }
	| {
			readonly pricingLine: undefined;
			readonly isInitialization: true;
			readonly checked: false;
	  };

export const NoPricingLine: CheckableLine = {
	pricingLine: undefined,
	isInitialization: true,
	checked: false,
};

interface Checkable {
	checked: boolean;
}

type CheckablePricingLine = ProductPricingLine & Checkable;

const getCheckedPricingLine = (pricingLines: CheckablePricingLine[]): CheckablePricingLine => {
	const checkedUnit = pricingLines.find((uom) => uom.checked);

	if (!checkedUnit) {
		throw new Error('Unable to determine the selected unit of measure');
	}

	return checkedUnit;
};

const getPricingLineDropdownText = (pricingLine: ProductPricingLine) => {
	const result = `${pricingLine.label} (${pricingLine.quantity} in ${pricingLine.unitOfMeasure.displayName})`;

	return result;
};

/**
 * Displays items required to Add To Cart
 */
export default function ProductPricingLines(props: ProductPricingLinesProps) {
	const [pricingLines, setPricingLines] = useState<CheckablePricingLine[]>([]);

	const handlePricingLineChange = (newLine: CheckablePricingLine): void => {
		const updatedPricingLines: CheckablePricingLine[] = pricingLines.map((line) => {
			return {
				...line,
				checked: line.id === newLine.id,
			};
		});

		setPricingLines(updatedPricingLines);

		props.onPricingLineChange({
			checked: newLine.checked,
			isInitialization: false,
			pricingLine: newLine,
		});
	};

	const { details, className } = props;

	useEffect(() => {
		if (details?.selected == null || !Array.isArray(details.available) || details.available.length === 0) {
			return;
		}

		const statefulPricingLines: CheckablePricingLine[] = details.available.map((line) => {
			const statefulLine: CheckablePricingLine = {
				...line,
				checked: line.id === details.selected!.id, // pre-select unit that was already loaded on the server-side
			};
			return statefulLine;
		});

		setPricingLines(statefulPricingLines);
		const line = getCheckedPricingLine(statefulPricingLines);
		props.onPricingLineChange({
			checked: true,
			isInitialization: false,
			pricingLine: line,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.details]);

	/**
	 * Event handler for blur and on change.
	 *
	 * @param {*} e
	 */
	const handleOnChangeAndBlur = (e) => {
		const newValue = e.currentTarget.value;
		const match = pricingLines.find((line) => line.id === newValue);

		if (match) {
			if (match.checked) {
				return;
			}
			match.checked = true;
			handlePricingLineChange(match);
		}
	};

	if (pricingLines.length <= 1) {
		return null;
	}

	const pricingLine = getCheckedPricingLine(pricingLines);

	return (
		<>
			<label className={className} htmlFor="unitOfMeasure">
				<span className="roc-visually-hidden">{Localizer('UnitOfMeasure')}</span>

				<select
					name="unitOfMeasure"
					id="unitOfMeasure"
					className={`roc-form-input__input roc-pdp__uom ${className}-select`}
					value={pricingLine.id}
					onChange={handleOnChangeAndBlur}
					onBlur={handleOnChangeAndBlur}
				>
					{pricingLines.map((line) => (
						<option value={line.id} key={line.id}>
							{getPricingLineDropdownText(line)}
						</option>
					))}
				</select>
			</label>
		</>
	);
}
